import { useMemo } from 'react'

import { useRouter } from 'next/router'

import type { UUID } from '@/types/Id'

interface UseProfileSidebarStateArgs {
  orgId: UUID | ''
  profileCompanyId: UUID | null | undefined
}

interface ProfileSidebarStateResult {
  /** True if viewing the profile setup page */
  isOnProfileSetupPage: boolean
  /** True if viewing the org's own company profile page */
  isViewingOwnCompany: boolean
  /** True if the Company Profile sidebar item should be active */
  isProfileActive: boolean
  /** True if on any companies page */
  isOnCompaniesPage: boolean
  /** True if the Companies sidebar item should be active (excludes own company) */
  isCompaniesActive: boolean
  /** The route for the Company Profile sidebar item */
  profileRoute: string
}

/**
 * Hook to determine active sidebar states for the Company Profile sidebar item.
 * Centralizes the logic for determining which sidebar items should be active
 * based on the current route and the org's company profile.
 */
export const useProfileSidebarState = ({
  orgId,
  profileCompanyId,
}: UseProfileSidebarStateArgs): ProfileSidebarStateResult => {
  const router = useRouter()
  const pathname = router.asPath.split('?')[0] // Remove query params

  return useMemo(() => {
    const profileSetupPath = `/orgs/${orgId}/library/profile`
    const ownCompanyPath = profileCompanyId
      ? `/orgs/${orgId}/library/companies/${profileCompanyId}`
      : null
    const companiesBasePath = `/orgs/${orgId}/library/companies`

    const isOnProfileSetupPage = pathname === profileSetupPath
    const isViewingOwnCompany = ownCompanyPath
      ? pathname.startsWith(ownCompanyPath)
      : false
    const isProfileActive = isOnProfileSetupPage || isViewingOwnCompany
    const isOnCompaniesPage = pathname.startsWith(companiesBasePath)
    const isCompaniesActive = isOnCompaniesPage && !isViewingOwnCompany

    const profileRoute = profileCompanyId
      ? `/orgs/${orgId}/library/companies/${profileCompanyId}`
      : `/orgs/${orgId}/library/profile`

    return {
      isOnProfileSetupPage,
      isViewingOwnCompany,
      isProfileActive,
      isOnCompaniesPage,
      isCompaniesActive,
      profileRoute,
    }
  }, [orgId, profileCompanyId, pathname])
}
